import {
  Alert,
  Avatar,
  Card,
  Col,
  DataTable,
  DetailDescriptions,
  Empty,
  Row,
  Space,
  Statistic,
  StatusTag,
  Tag,
  Typography,
  ViewButton,
} from '@wanjianshi/admin-ui';
import { formatDateTime, formatNumber } from '@wanjianshi/shared-utils';
import type { TableColumnsType } from 'antd';
import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAdminPage } from '@/hooks/useAdminResource';
import {
  billingCycleLabels,
  creditStatusLabels,
  creditTransactionTypeLabels,
  paymentMethodLabels,
  paymentStatusLabels,
  subscriptionStatusLabels,
  teamMemberRoleLabels,
  type TeamCreditSummary,
  type TeamCreditTransactionRow,
  type TeamMemberRow,
  type TeamProjectRow,
  type TeamSubscriptionPaymentRow,
  type TeamSubscriptionRow,
} from './teamDetail';

function textValue(value: unknown): string {
  return value === null || value === undefined || value === '' ? '—' : String(value);
}

function yuan(value: unknown): string {
  const amount = Number(value);
  return Number.isFinite(amount) ? `¥${formatNumber(amount)}` : '—';
}

function normalStatusTag(value?: number | null) {
  if (value === null || value === undefined) return '—';
  const normal = Number(value) === 0;
  return <StatusTag value={value} label={normal ? '正常' : '停用'} color={normal ? 'success' : 'default'} />;
}

function subscriptionStatusTag(value?: string | null) {
  const normalized = String(value ?? '').toLowerCase();
  return <StatusTag value={normalized} label={subscriptionStatusLabels[normalized] ?? '未知状态'} />;
}

function paymentStatusTag(value?: string | null) {
  const normalized = String(value ?? '').toLowerCase();
  return <StatusTag value={normalized} label={paymentStatusLabels[normalized] ?? '未知状态'} />;
}

function PanelError({ error, onRetry }: { error: string | null; onRetry: () => void }) {
  return error ? (
    <Alert
      showIcon
      type="error"
      message={error}
      action={<a onClick={() => void onRetry()}>重试</a>}
    />
  ) : null;
}

export function TeamMembersPanel({ teamId }: { teamId: string }) {
  const resource = useAdminPage<TeamMemberRow>(`/admin/teams/${teamId}/members`, 20);
  const columns = useMemo<TableColumnsType<TeamMemberRow>>(() => [
    {
      title: '成员',
      key: 'member',
      fixed: 'left',
      width: 230,
      render: (_value, row) => (
        <Space size={10}>
          <Avatar src={row.user_avatar || row.avatar || undefined}>
            {(row.user_nickname || row.nickname || '?').slice(0, 1)}
          </Avatar>
          <div className="team-detail-person">
            <Typography.Text strong>{row.user_nickname || row.nickname || '未设置昵称'}</Typography.Text>
            <Typography.Text type="secondary">
              {row.user_username ? `账号：${row.user_username}` : `用户 ID：${row.user_id}`}
            </Typography.Text>
          </div>
        </Space>
      ),
    },
    {
      title: '联系方式',
      key: 'contact',
      width: 190,
      render: (_value, row) => (
        <div className="team-detail-stacked-text">
          <span>{textValue(row.user_mobile_masked)}</span>
          <Typography.Text type="secondary">{textValue(row.user_email_masked)}</Typography.Text>
        </div>
      ),
    },
    { title: '团队内昵称', dataIndex: 'nickname', key: 'nickname', width: 130, render: textValue },
    {
      title: '团队角色',
      dataIndex: 'role',
      key: 'role',
      width: 110,
      render: value => <Tag color={value === 'founder' ? 'blue' : value === 'admin' ? 'geekblue' : undefined}>{teamMemberRoleLabels[String(value)] ?? textValue(value)}</Tag>,
    },
    { title: '成员状态', dataIndex: 'status', key: 'status', width: 100, render: normalStatusTag },
    { title: '账户状态', dataIndex: 'user_status', key: 'user_status', width: 100, render: normalStatusTag },
    {
      title: '成员积分',
      key: 'credits',
      width: 190,
      render: (_value, row) => (
        <div className="team-detail-stacked-text">
          <span>可用 {formatNumber(row.available_credits)}</span>
          <Typography.Text type="secondary">
            已分配 {formatNumber(row.allocated_credits)} / 已使用 {formatNumber(row.member_used_credits)}
          </Typography.Text>
        </div>
      ),
    },
    {
      title: '本月积分',
      key: 'monthly_credits',
      width: 160,
      render: (_value, row) => `${formatNumber(row.monthly_used)} / ${row.monthly_limit == null ? '不限' : formatNumber(row.monthly_limit)}`,
    },
    {
      title: '积分账户',
      dataIndex: 'credit_status',
      key: 'credit_status',
      width: 110,
      render: value => value
        ? <StatusTag value={String(value)} label={creditStatusLabels[String(value)] ?? textValue(value)} />
        : <Tag>未建立</Tag>,
    },
    { title: '加入时间', dataIndex: 'joined_at', key: 'joined_at', width: 180, render: formatDateTime },
    { title: '更新时间', dataIndex: 'update_time', key: 'update_time', width: 180, render: formatDateTime },
    { title: '成员记录 ID', dataIndex: 'id', key: 'id', width: 170, render: textValue },
  ], []);

  return (
    <Card title={`团队成员（${resource.total}）`} className="management-list-card">
      <PanelError error={resource.error} onRetry={resource.reload} />
      <DataTable<TeamMemberRow>
        columns={columns}
        dataSource={resource.items}
        loading={resource.loading}
        rowKey={row => String(row.id)}
        pagination={{
          current: resource.page,
          pageSize: resource.pageSize,
          total: resource.total,
          showSizeChanger: true,
        }}
        onChange={pagination => {
          const nextPageSize = pagination.pageSize ?? resource.pageSize;
          if (nextPageSize !== resource.pageSize) resource.setPageSize(nextPageSize);
          resource.setPage(pagination.current ?? 1);
        }}
      />
    </Card>
  );
}

export function TeamSubscriptionPanel({ teamId }: { teamId: string }) {
  const subscriptions = useAdminPage<TeamSubscriptionRow>(`/admin/teams/${teamId}/subscription`, 100);
  const payments = useAdminPage<TeamSubscriptionPaymentRow>(`/admin/team-memberships/${teamId}/payments`, 20);
  const current = subscriptions.items.find(item => item.is_current) ?? subscriptions.items[0];

  const subscriptionColumns = useMemo<TableColumnsType<TeamSubscriptionRow>>(() => [
    { title: '记录', key: 'current', width: 90, render: (_value, row) => row.is_current ? <Tag color="blue">当前</Tag> : <Tag>历史</Tag> },
    { title: '套餐', dataIndex: 'plan_name', key: 'plan_name', width: 130, render: textValue },
    { title: '状态', dataIndex: 'status', key: 'status', width: 100, render: subscriptionStatusTag },
    { title: '计费周期', dataIndex: 'billing_cycle', key: 'billing_cycle', width: 100, render: value => billingCycleLabels[String(value)] ?? textValue(value) },
    { title: '购买席位', dataIndex: 'member_count', key: 'member_count', width: 100, render: formatNumber },
    { title: '有效席位上限', dataIndex: 'max_members', key: 'max_members', width: 120, render: formatNumber },
    { title: '套餐单价（元）', dataIndex: 'price', key: 'price', width: 125, render: yuan },
    { title: '席位总价（元）', dataIndex: 'total_price', key: 'total_price', width: 125, render: yuan },
    { title: '每期积分', dataIndex: 'credits_per_period', key: 'credits_per_period', width: 110, render: formatNumber },
    { title: '积分存储上限', dataIndex: 'max_credits_stored', key: 'max_credits_stored', width: 120, render: formatNumber },
    { title: '累计获得积分', dataIndex: 'total_credits_earned', key: 'total_credits_earned', width: 120, render: formatNumber },
    { title: '累计使用积分', dataIndex: 'total_credits_used', key: 'total_credits_used', width: 120, render: formatNumber },
    { title: '开始时间', dataIndex: 'started_at', key: 'started_at', width: 180, render: formatDateTime },
    { title: '到期时间', dataIndex: 'current_period_end', key: 'current_period_end', width: 180, render: formatDateTime },
    { title: '订阅记录 ID', dataIndex: 'id', key: 'id', width: 170, render: textValue },
  ], []);

  const paymentColumns = useMemo<TableColumnsType<TeamSubscriptionPaymentRow>>(() => [
    { title: '支付流水号', dataIndex: 'payment_no', key: 'payment_no', width: 190, render: textValue },
    { title: '套餐', dataIndex: 'vip_package_name', key: 'vip_package_name', width: 130, render: textValue },
    { title: '支付状态', dataIndex: 'status', key: 'status', width: 100, render: paymentStatusTag },
    { title: '支付方式', dataIndex: 'pay_method', key: 'pay_method', width: 110, render: value => paymentMethodLabels[String(value)] ?? textValue(value) },
    { title: '金额（元）', dataIndex: 'amount', key: 'amount', width: 110, render: yuan },
    { title: '席位数', dataIndex: 'member_count', key: 'member_count', width: 90, render: formatNumber },
    { title: '计费周期', dataIndex: 'billing_cycle', key: 'billing_cycle', width: 100, render: value => billingCycleLabels[String(value)] ?? textValue(value) },
    { title: '发放积分', dataIndex: 'credits_added', key: 'credits_added', width: 110, render: formatNumber },
    { title: '扣费用户 ID', dataIndex: 'user_id', key: 'user_id', width: 170, render: textValue },
    { title: '第三方流水号', dataIndex: 'trade_no', key: 'trade_no', width: 180, render: textValue },
    { title: '支付时间', dataIndex: 'paid_time', key: 'paid_time', width: 180, render: formatDateTime },
    { title: '错误信息', dataIndex: 'error_message', key: 'error_message', width: 220, ellipsis: true, render: textValue },
  ], []);

  return (
    <div className="team-detail-panel-stack">
      <Card title="当前会员套餐" loading={subscriptions.loading && !current}>
        <PanelError error={subscriptions.error} onRetry={subscriptions.reload} />
        {current ? (
          <DetailDescriptions
            column={{ xs: 1, sm: 2, lg: 4 }}
            items={[
              { key: 'plan', label: '套餐名称', children: current.plan_name || '—' },
              { key: 'status', label: '套餐状态', children: subscriptionStatusTag(current.status) },
              { key: 'cycle', label: '计费周期', children: billingCycleLabels[current.billing_cycle] ?? current.billing_cycle },
              { key: 'remaining', label: '剩余天数', children: current.status === 'active' ? `${formatNumber(current.days_until_expiry)} 天` : '—' },
              { key: 'seats', label: '购买席位数', children: formatNumber(current.member_count) },
              { key: 'max_members', label: '有效席位上限', children: formatNumber(current.max_members) },
              { key: 'price', label: '套餐单价', children: yuan(current.price) },
              { key: 'total_price', label: '席位总价', children: yuan(current.total_price) },
              { key: 'credits', label: '每期发放积分', children: formatNumber(current.credits_per_period) },
              { key: 'monthly_credits', label: '每月发放积分', children: formatNumber(current.credits_per_month) },
              { key: 'credit_cap', label: '积分存储上限', children: formatNumber(current.max_credits_stored) },
              { key: 'months_issued', label: '已发放月数', children: formatNumber(current.months_issued) },
              { key: 'period_start', label: '当前周期开始', children: formatDateTime(current.current_period_start) },
              { key: 'period_end', label: '当前周期结束', children: formatDateTime(current.current_period_end) },
              { key: 'earned', label: '累计获得积分', children: formatNumber(current.total_credits_earned) },
              { key: 'used', label: '累计使用积分', children: formatNumber(current.total_credits_used) },
              { key: 'subscription_id', label: '订阅记录 ID', children: textValue(current.id) },
              { key: 'plan_id', label: '套餐 ID', children: textValue(current.plan_id) },
              { key: 'created', label: '创建时间', children: formatDateTime(current.create_time) },
              { key: 'updated', label: '更新时间', children: formatDateTime(current.update_time) },
            ]}
          />
        ) : subscriptions.error ? null : <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="该团队尚未开通会员套餐" />}
      </Card>

      <Card title={`套餐记录（${subscriptions.total}）`} className="management-list-card">
        <DataTable<TeamSubscriptionRow>
          columns={subscriptionColumns}
          dataSource={subscriptions.items}
          loading={subscriptions.loading}
          rowKey={row => String(row.id)}
          pagination={false}
        />
      </Card>

      <Card title={`支付记录（${payments.total}）`} className="management-list-card">
        <PanelError error={payments.error} onRetry={payments.reload} />
        <DataTable<TeamSubscriptionPaymentRow>
          columns={paymentColumns}
          dataSource={payments.items}
          loading={payments.loading}
          rowKey={row => String(row.id)}
          pagination={{ current: payments.page, pageSize: payments.pageSize, total: payments.total }}
          onChange={pagination => {
            const nextPageSize = pagination.pageSize ?? payments.pageSize;
            if (nextPageSize !== payments.pageSize) payments.setPageSize(nextPageSize);
            payments.setPage(pagination.current ?? 1);
          }}
        />
      </Card>
    </div>
  );
}

export function TeamCreditsPanel({ teamId }: { teamId: string }) {
  const summary = useAdminPage<TeamCreditSummary>(`/admin/teams/${teamId}/credits`, 1);
  const transactions = useAdminPage<TeamCreditTransactionRow>(`/admin/teams/${teamId}/credit-transactions`, 20);
  const credit = summary.items[0];

  const columns = useMemo<TableColumnsType<TeamCreditTransactionRow>>(() => [
    { title: '流水 ID', dataIndex: 'id', key: 'id', width: 170, render: textValue },
    {
      title: '变动类型',
      dataIndex: 'type',
      key: 'type',
      width: 130,
      render: value => <Tag>{creditTransactionTypeLabels[String(value)] ?? textValue(value)}</Tag>,
    },
    {
      title: '变动积分',
      dataIndex: 'amount',
      key: 'amount',
      width: 110,
      align: 'right',
      render: value => {
        const amount = Number(value ?? 0);
        return <Typography.Text className={amount > 0 ? 'team-credit-amount--positive' : amount < 0 ? 'team-credit-amount--negative' : undefined}>{amount > 0 ? '+' : ''}{formatNumber(amount)}</Typography.Text>;
      },
    },
    {
      title: '变动前 / 变动后',
      key: 'balance',
      width: 160,
      render: (_value, row) => `${formatNumber(row.balance_before)} → ${formatNumber(row.balance_after)}`,
    },
    {
      title: '操作用户',
      key: 'user',
      width: 180,
      render: (_value, row) => (
        <div className="team-detail-stacked-text">
          <span>{row.user_nickname || (row.user_id ? `用户 #${row.user_id}` : '系统')}</span>
          <Typography.Text type="secondary">{textValue(row.user_mobile_masked)}</Typography.Text>
        </div>
      ),
    },
    {
      title: '关联项目',
      key: 'project',
      width: 180,
      render: (_value, row) => row.project_name || (row.project_id ? `项目 #${row.project_id}` : '—'),
    },
    {
      title: '任务 / 子任务',
      key: 'task',
      width: 220,
      render: (_value, row) => (
        <div className="team-detail-stacked-text">
          <span>{textValue(row.task_id)}</span>
          <Typography.Text type="secondary">{row.item_id ? `子任务：${row.item_id}` : '—'}</Typography.Text>
        </div>
      ),
    },
    { title: '说明', dataIndex: 'description', key: 'description', width: 240, ellipsis: true, render: textValue },
    { title: '订阅 ID', dataIndex: 'subscription_id', key: 'subscription_id', width: 170, render: textValue },
    { title: '分配记录 ID', dataIndex: 'allocation_id', key: 'allocation_id', width: 170, render: textValue },
    { title: '幂等键', dataIndex: 'idempotency_key', key: 'idempotency_key', width: 220, ellipsis: true, render: textValue },
    { title: '发生时间', dataIndex: 'create_time', key: 'create_time', width: 180, render: formatDateTime },
  ], []);

  return (
    <div className="team-detail-panel-stack">
      <PanelError error={summary.error} onRetry={summary.reload} />
      <Row gutter={[16, 16]}>
        {[
          { title: '当前可用积分', value: credit?.stored_credits ?? 0 },
          { title: '团队总积分', value: credit?.total_credits ?? 0 },
          { title: '成员冻结积分', value: credit?.frozen_credits ?? 0 },
          { title: '团队累计已用', value: credit?.used_credits ?? 0 },
        ].map(item => (
          <Col xs={24} sm={12} xl={6} key={item.title}>
            <Card size="small" className="management-metric-card" loading={summary.loading && !credit}>
              <Statistic title={item.title} value={item.value} formatter={value => formatNumber(String(value))} />
            </Card>
          </Col>
        ))}
      </Row>

      <Card title="成员积分汇总">
        {credit ? (
          <DetailDescriptions
            column={{ xs: 1, sm: 2, lg: 4 }}
            items={[
              { key: 'members', label: '已有积分账户成员', children: formatNumber(credit.members_with_credit) },
              { key: 'allocated', label: '成员累计分配', children: formatNumber(credit.member_allocated_credits) },
              { key: 'available', label: '成员当前可用', children: formatNumber(credit.member_available_credits) },
              { key: 'used', label: '成员累计已用', children: formatNumber(credit.member_used_credits) },
            ]}
          />
        ) : summary.error ? null : <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="暂无团队积分数据" />}
      </Card>

      <Card title={`积分流水（${transactions.total}）`} className="management-list-card">
        <PanelError error={transactions.error} onRetry={transactions.reload} />
        <DataTable<TeamCreditTransactionRow>
          columns={columns}
          dataSource={transactions.items}
          loading={transactions.loading}
          rowKey={row => String(row.id)}
          pagination={{ current: transactions.page, pageSize: transactions.pageSize, total: transactions.total }}
          onChange={pagination => {
            const nextPageSize = pagination.pageSize ?? transactions.pageSize;
            if (nextPageSize !== transactions.pageSize) transactions.setPageSize(nextPageSize);
            transactions.setPage(pagination.current ?? 1);
          }}
        />
      </Card>
    </div>
  );
}

export function TeamProjectsPanel({ teamId }: { teamId: string }) {
  const navigate = useNavigate();
  const resource = useAdminPage<TeamProjectRow>(`/admin/teams/${teamId}/projects`, 20);
  const columns = useMemo<TableColumnsType<TeamProjectRow>>(() => [
    {
      title: '项目',
      key: 'project',
      fixed: 'left',
      width: 260,
      render: (_value, row) => (
        <Space size={10}>
          <Avatar shape="square" src={row.cover || undefined}>{row.name.slice(0, 1)}</Avatar>
          <div className="team-detail-person">
            <Typography.Text strong>{row.name}</Typography.Text>
            <Typography.Text type="secondary">项目 ID：{row.id}</Typography.Text>
          </div>
        </Space>
      ),
    },
    { title: '项目描述', dataIndex: 'description', key: 'description', width: 220, ellipsis: true, render: textValue },
    {
      title: '所有者',
      key: 'owner',
      width: 200,
      render: (_value, row) => (
        <div className="team-detail-stacked-text">
          <span>{row.owner_nickname || row.owner_username || `用户 #${row.owner_user_id}`}</span>
          <Typography.Text type="secondary">{textValue(row.owner_mobile_masked)}</Typography.Text>
        </div>
      ),
    },
    { title: '画布数', dataIndex: 'canvas_count', key: 'canvas_count', width: 90, align: 'right', render: formatNumber },
    { title: '生成任务数', dataIndex: 'task_count', key: 'task_count', width: 110, align: 'right', render: formatNumber },
    { title: '团队文件夹 ID', dataIndex: 'team_folder_id', key: 'team_folder_id', width: 150, render: textValue },
    { title: '项目文件夹 ID', dataIndex: 'folder_id', key: 'folder_id', width: 150, render: textValue },
    { title: '加入团队时间', dataIndex: 'team_project_create_time', key: 'team_project_create_time', width: 180, render: formatDateTime },
    { title: '项目创建时间', dataIndex: 'created_at', key: 'created_at', width: 180, render: formatDateTime },
    { title: '项目更新时间', dataIndex: 'updated_at', key: 'updated_at', width: 180, render: formatDateTime },
    { title: '团队项目记录 ID', dataIndex: 'team_project_id', key: 'team_project_id', width: 180, render: textValue },
    { title: '创建人 ID', dataIndex: 'created_by', key: 'created_by', width: 170, render: textValue },
    {
      title: '操作',
      key: 'action',
      fixed: 'right',
      width: 90,
      render: (_value, row) => <ViewButton onClick={() => navigate(`/projects/${row.id}`)}>详情</ViewButton>,
    },
  ], [navigate]);

  return (
    <Card title={`团队项目（${resource.total}）`} className="management-list-card">
      <PanelError error={resource.error} onRetry={resource.reload} />
      <DataTable<TeamProjectRow>
        columns={columns}
        dataSource={resource.items}
        loading={resource.loading}
        rowKey={row => String(row.team_project_id)}
        pagination={{ current: resource.page, pageSize: resource.pageSize, total: resource.total }}
        onChange={pagination => {
          const nextPageSize = pagination.pageSize ?? resource.pageSize;
          if (nextPageSize !== resource.pageSize) resource.setPageSize(nextPageSize);
          resource.setPage(pagination.current ?? 1);
        }}
      />
    </Card>
  );
}
